import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { Selvityspyynto } from '@lemon/core-firebase/model/selvitettavat'
import { WebSelvityspyyntoTallennaPyynto, WebSelvityspyyntoTallennaVastaus } from '@lemon/core-firebase/model/web-tosite-api'
import { TositeKopioija } from '@lemon/core-firebase/service/tosite/tosite.kopioija'
import { firstValueFrom } from 'rxjs'
import { take } from 'rxjs/operators'
import { TositeMuokkaaComponentData, TositeMuokkaaComponentDataResolve } from '../../../_angular/_resolvers/tosite.resolve'
import { FirebaseLemonaid } from '../../../_angular/service/firebase-lemonaid.service'
import { KayttajaService } from '../../../_angular/service/kayttaja.service'
import { TositeService } from '../../../_angular/service/tosite/tosite.service'
import { asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LemonTranslationService } from '../../../_jaettu-angular/service/lemon-translation.service'
import { TositeKuvaCacheService } from '../../kuvat/tosite-kuva-cache.service'
import { TositeLukittuDialog } from '../../tosite-lukittu.dialog'

@Component({
  selector: 'app-selvitys-kortti',
  templateUrl: './selvitys-kortti.component.html',
  styleUrls: ['./selvitys-kortti.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelvitysKorttiComponent implements OnInit {
  @Input() kortti!: Selvityspyynto
  @Input() yritysmuoto: Yritysmuoto | null = null

  @ViewChild('tositeInput') tositeInput: ElementRef<HTMLInputElement>

  vastaus = form(signal(''))
  readonly tallennustila = signal<'lomake' | 'tallentaa' | 'tallennettu'>('lomake')
  muuKasittelyAuki = false
  hukkunutAuki = false
  hukkunutSelite = form(signal(''))
  omaHankintaAuki = false
  eiTositettaAuki = false
  velallinen = form(signal(''))
  valittuKasittelytapaAvain: string | null = null
  readonly esikatselukuvaUrl = signal<string | null>(null)
  readonly kuvaUrlit = signal<string[]>([])
  kuvaAuki = false
  readonly virheAvain = signal<string | null>(null)

  readonly TOIMINIMI = Yritysmuoto.TOIMINIMI

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _kayttajaService: KayttajaService,
    private _translationService: LemonTranslationService,
    private _router: Router,
    private _tositeService: TositeService,
    private _tositeKopioija: TositeKopioija,
    private _tositeMuokkaaResolve: TositeMuokkaaComponentDataResolve,
    private _tositeKuvaCacheService: TositeKuvaCacheService,
    private _dialog: MatDialog,
  ) {}

  async ngOnInit(): Promise<void> {
    const tositeAvain = this.kortti.tositeAvaimet?.[0]
    if (!tositeAvain) {
      return
    }
    const tosite = await firstValueFrom(this._tositeService.annaKuittiObservable(tositeAvain).pipe(take(1)))
    if (!tosite) {
      return
    }
    const kayttajanTiedot = await this._kayttajaService.getKayttajanTiedot()
    this.esikatselukuvaUrl.set(this._tositeKuvaCacheService.annaKuvienUrlit(tosite, kayttajanTiedot, 'thumb')[0] ?? null)
    this.kuvaUrlit.set(this._tositeKuvaCacheService.annaKuvienUrlit(tosite, kayttajanTiedot, 'full'))
  }

  avaaKuva() {
    if (this.kuvaUrlit().length > 0) {
      this.kuvaAuki = true
    }
  }

  @HostListener('document:keydown.escape')
  suljeKuva() {
    this.kuvaAuki = false
  }

  get vastausPituus(): number {
    return this.vastaus().value().trim().length
  }

  get hukkunutSelitePituus(): number {
    return this.hukkunutSelite().value().trim().length
  }

  get velallinenPituus() {
    return this.velallinen().value().trim().length
  }

  avaaTositteenLisays(): void {
    this.tositeInput.nativeElement.click()
  }

  tositeTiedostotMuuttui(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files || input.files.length === 0) {
      return
    }
    const tiedostot = Array.from(input.files)
    input.value = ''
    this._router.navigate(['/tositteet/osto/lisaa'], {
      state: {
        selvitysTiedostot: tiedostot,
        selvitettava: {
          selvityspyynnonAvain: this.kortti.avain,
          maksutapa: this.kortti.maksutapa ?? null,
          summa: this.kortti.summa ?? null,
          kirjausrivinPuoli: this.kortti.kirjausrivinPuoli ?? null,
          pvml: this.kortti.pvml ?? null,
        },
      },
    })
  }

  async avaaTositteenMuokkaus(): Promise<void> {
    const tositeAvain = this.kortti.tositeAvaimet?.[0]
    if (!tositeAvain) {
      return
    }

    const tosite = await firstValueFrom(this._tositeService.annaKuittiObservable(tositeAvain).pipe(take(1)))
    if (!tosite) {
      return
    }

    if (tosite.l) {
      this._dialog.open(TositeLukittuDialog)
      return
    }

    const kopio = this._tositeKopioija.kopioiTosite(tosite)
    const data: TositeMuokkaaComponentData = { tosite: kopio, selvityspyynnonAvain: this.kortti.avain }
    this._tositeMuokkaaResolve.asetaOlemassaolevaData(data)

    if (this._tositeService.getMaksutapaNameForRouter(tosite.maksutapa) === 'osto') {
      this._router.navigate(['tositteet', 'osto', 'muokkaa', tositeAvain])
    } else {
      this._router.navigate(['tositteet', 'muokkaus', tositeAvain])
    }
  }

  async valitseKasittelytapa(avain: string) {
    this.valittuKasittelytapaAvain = avain
    asetaArvo(this.vastaus, this._translationService.lokalisoi(avain))
    await this.tallennaVastaus()
  }

  async tallennaEiTositetta() {
    this.valittuKasittelytapaAvain = 'tositteet.selvitys.ei-tositetta'
    asetaArvo(this.vastaus, this._translationService.lokalisoi('tositteet.selvitys.ei-tositetta'))
    await this.tallennaVastaus()
  }

  async tallennaHukkunut() {
    if (this.hukkunutSelitePituus === 0) {
      return
    }
    this.valittuKasittelytapaAvain = 'tositteet.selvitys.tosite-hukkunut'
    asetaArvo(this.vastaus, `${this._translationService.lokalisoi('tositteet.selvitys.tosite-hukkunut')}: ${this.hukkunutSelite().value().trim()}`)
    await this.tallennaVastaus()
  }

  async tallennaOmaHankinta() {
    if (this.velallinenPituus === 0) {
      return
    }
    this.valittuKasittelytapaAvain = 'tositteet.selvitys.oma-hankinta'
    asetaArvo(
      this.vastaus,
      `${this._translationService.lokalisoi('tositteet.selvitys.ei-yrityksen-kulu')}. ${this._translationService.lokalisoi('tositteet.selvitys.velallinen')}: ${this.velallinen().value().trim()}`,
    )
    await this.tallennaVastaus()
  }

  async tallennaVastaus(): Promise<void> {
    if (this.vastausPituus === 0 || this.tallennustila() !== 'lomake') {
      return
    }
    this.tallennustila.set('tallentaa')
    this.virheAvain.set(null)
    try {
      const pyynto: WebSelvityspyyntoTallennaPyynto = { avain: this.kortti.avain, vastaus: this.vastaus().value().trim() }
      const vastaus = await this._firebaseLemonaid.functionsCall<WebSelvityspyyntoTallennaPyynto, WebSelvityspyyntoTallennaVastaus>(
        'tositeSelvityspyyntoTallennaWeb',
        pyynto,
        undefined,
        'europe-north1',
      )
      if (vastaus.e) {
        console.error(`Selvityspyynnön ${pyynto.avain} vastauksen tallennus epäonnistui: ${vastaus.e}`)
        this.virheAvain.set(vastaus.e === 'not-found' ? 'tositteet.selvitys.pyynto-poistettu' : 'tositteet.selvitys.tallennus-epaonnistui')
        this.tallennustila.set('lomake')
        return
      }
      this.tallennustila.set('tallennettu')
    } catch (err) {
      this.virheAvain.set('tositteet.selvitys.tallennus-epaonnistui')
      this.tallennustila.set('lomake')
      throw err
    }
  }
}
